import { useState } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { Trash2, AlertTriangle } from 'lucide-react';
import { getAddrCoord, isAddressableKind } from '@/hooks/useTombstone';
import { cn } from '@/lib/utils';

interface DeleteEventDialogProps {
  event: NostrEvent;
  label: string;
  addrCoord?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}

export function DeleteEventDialog({
  event,
  label,
  addrCoord,
  open,
  onOpenChange,
  onDeleted,
}: DeleteEventDialogProps) {
  const [reason, setReason] = useState('');
  const { mutateAsync: publish, isPending } = useNostrPublish();
  const { toast } = useToast();

  const handleDelete = async () => {
    try {
      const tags: string[][] = [['k', String(event.kind)]];

      if (isAddressableKind(event.kind) && addrCoord) {
        tags.push(['a', addrCoord]);
      } else {
        tags.push(['e', event.id]);
      }

      await publish({
        kind: 5,
        content: reason.trim(),
        tags,
      });

      toast({
        title: 'Deletion request sent',
        description: 'A NIP-09 deletion event was published. Some relays may not honor this request.',
      });

      onDeleted?.();
      onOpenChange(false);
      setReason('');
    } catch (err) {
      toast({
        title: 'Failed to send deletion',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const previewId = event.id.slice(0, 16) + '…';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded flex items-center justify-center bg-destructive/10 border border-destructive/20">
              <Trash2 className="w-4 h-4 text-destructive" />
            </div>
            <DialogTitle className="font-display text-base">
              Delete {label}
            </DialogTitle>
          </div>
          <DialogDescription className="text-muted-foreground text-sm leading-relaxed">
            This publishes a <span className="text-foreground font-medium">NIP-09 deletion request</span> (kind 5).
            Compliant relays and clients will honor this — but deletion is not guaranteed across all
            relays and cannot be reversed.
          </DialogDescription>
        </DialogHeader>

        {/* Event preview */}
        <div className="rounded border border-border bg-secondary/30 p-3 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Event ID</span>
            <code className="text-xs text-foreground font-mono">{previewId}</code>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Kind</span>
            <span className="text-xs text-foreground">{event.kind}</span>
          </div>
          {addrCoord && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Address</span>
              <span className="text-xs text-foreground font-mono truncate max-w-[180px]">{addrCoord}</span>
            </div>
          )}
          {isAddressableKind(event.kind) && (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-400">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>Addressable event — deletion targets all versions via the address tag.</span>
            </div>
          )}
        </div>

        {/* Reason */}
        <div className="space-y-2">
          <Label htmlFor="reason" className="text-sm text-muted-foreground">
            Reason <span className="text-muted-foreground/60">(optional)</span>
          </Label>
          <Textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. published by mistake"
            className="bg-secondary/30 border-border resize-none text-sm h-20"
          />
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            className="text-muted-foreground"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90 tombstone-glow-red"
          >
            {isPending ? (
              <>
                <span className="mr-2 w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Trash2 className="w-3.5 h-3.5 mr-2" />
                Send Deletion Request
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
